// The goal Timeline's grouping — import-free (only the shared schedule rule), so a jest test can exercise it
// without the modal's dependency graph, and so iOS/Android mirror one function rather than a component.
import { effectiveStartMs, gatingBlocker, type SchedulableTask } from './task-schedule';

/** The minimum a plan row needs to be placed on the timeline. `WebGoalTask` satisfies it. */
export interface TimelineTask extends SchedulableTask {
  id: string;
  title: string;
}

/** One day of the timeline: its tasks, dated ones first, then the gated ones that inherit the day. */
export interface TimelineDay<T extends TimelineTask = TimelineTask> {
  /** Local midnight, ms — the group key and the sort key. */
  dayMs: number;
  dated: T[];
  gated: { task: T; after: string }[];
}

/**
 * The plan grouped by the LOCAL calendar day each task can start: its own `notBefore`, else the latest
 * `notBefore` among the tasks it waits for (`effectiveStartMs`, one hop). Tasks with neither are not in the
 * timeline — they stay in the plan. Exported for the test; the same grouping is what iOS and Android draw.
 */
export function goalTimelineDays<T extends TimelineTask>(tasks: readonly T[], now: number = Date.now()): TimelineDay<T>[] {
  const byDay = new Map<number, TimelineDay<T>>();
  for (const task of tasks) {
    const at = effectiveStartMs(task, now);
    if (at === null) continue;
    const day = new Date(at);
    day.setHours(0, 0, 0, 0);
    const key = day.getTime();
    let group = byDay.get(key);
    if (!group) {
      group = { dayMs: key, dated: [], gated: [] };
      byDay.set(key, group);
    }
    const blocker = task.notBefore ? undefined : gatingBlocker(task, now);
    if (blocker) group.gated.push({ task, after: blocker.title });
    else group.dated.push(task);
  }
  return [...byDay.values()].sort((a, b) => a.dayMs - b.dayMs);
}

